import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Icon } from "@animech/ui";

export type RoomPanelVariant = "desktop" | "mobile";

const ink = "#101010";
const secondary = "#857d77";
const muted = "#6a6a6a";
const openFill = "rgb(240, 240, 240)";
const tileHoverFill = "rgb(248, 248, 248)";
const cardShadow = "0 8px 24px -8px rgba(0, 0, 0, 0.28)";
const easing = "cubic-bezier(0.2, 0.7, 0.2, 1)";
const selectedRing = `inset 0 0 0 2px #fff, 0 0 0 1px ${ink}`;
const unselectedRing = "inset 0 0 0 1px rgba(0, 0, 0, 0.12)";

const focusRing = css`
	&:focus-visible {
		outline: 2px solid ${ink};
		outline-offset: 2px;
	}
`;

const popoverEnter = keyframes`
	from {
		opacity: 0;
		transform: translateY(6px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const cardEnter = keyframes`
	from {
		opacity: 0;
		transform: translateY(calc(-50% + 12px));
	}
	to {
		opacity: 1;
		transform: translateY(-50%);
	}
`;

export const HomeIcon = styled(Icon)`
	font-size: 20px;
`;

export const Anchor = styled.div`
	position: relative;
	display: flex;
`;

export const PillLabel = styled.span``;

export const Pill = styled.button`
	${({ theme }) => css`
		display: inline-flex;
		align-items: center;
		gap: 8px;
		height: 38px;
		padding: 0 14px;
		border: 0;
		border-radius: ${theme.dimensions.borderRadius};
		background: ${theme.colors.backgrounds[0].background};
		color: ${ink};
		font-family: inherit;
		cursor: pointer;
		${focusRing}

		${PillLabel} {
			font-size: ${theme.typography.fontSize.base};
			font-weight: ${theme.typography.fontWeight.light};
			text-transform: uppercase;
			letter-spacing: 0.14em;
			line-height: 1;
		}

		&:hover {
			background: ${theme.colors.backgrounds[1].background};
		}

		&[data-open="true"] {
			background: ${openFill};
		}
	`}
`;

export const Header = styled.div``;
export const Title = styled.span``;
export const CloseButton = styled.button``;
export const Body = styled.div``;
export const Section = styled.section``;
export const SectionLabelRow = styled.div``;
export const SectionLabel = styled.span``;
export const SelectedName = styled.span``;
export const Caption = styled.div``;
export const Divider = styled.hr``;
export const Swatches = styled.ul``;
export const Swatch = styled.button``;
export const Tiles = styled.ul``;
export const Tile = styled.button``;
export const Thumbnail = styled.span``;
export const TileLabel = styled.span``;

export const Panel = styled.div<{ variant: RoomPanelVariant }>`
	${({ theme, variant }) => {
		const mobile = variant === "mobile";
		const hairline = theme.colors.backgrounds[2].background;

		return css`
			box-sizing: border-box;
			border: 1px solid ${theme.colors.backgrounds[0].border};
			border-radius: ${theme.dimensions.borderRadius};
			box-shadow: ${cardShadow};
			color: ${ink};
			font-weight: ${theme.typography.fontWeight.light};
			line-height: normal;
			text-align: left;

			${mobile
				? css`
						position: absolute;
						left: 16px;
						right: 16px;
						top: 50%;
						transform: translateY(-50%);
						display: flex;
						flex-direction: column;
						gap: 18px;
						padding: 16px;
						background: rgba(255, 255, 255, 0.94);
						animation: ${cardEnter} 200ms ${easing};
					`
				: css`
						position: absolute;
						bottom: calc(100% + ${theme.app.viewport.padding});
						right: 0;
						width: 344px;
						background: ${theme.colors.backgrounds[0].background};
						animation: ${popoverEnter} 180ms ${easing};
					`}

			${Header} {
				display: flex;
				align-items: center;
				gap: ${theme.dimensions.margin.small};
				${!mobile &&
				css`
					padding: 14px 16px;
					border-bottom: 1px solid ${hairline};
				`}
			}

			${Title} {
				flex: 1;
				font-size: ${theme.typography.fontSize.xxxLarge};
			}

			${CloseButton} {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				flex: none;
				width: ${mobile ? "38px" : "24px"};
				height: ${mobile ? "38px" : "24px"};
				padding: 0;
				border: 0;
				border-radius: ${mobile ? "50%" : theme.dimensions.borderRadius};
				background: transparent;
				color: ${muted};
				font-size: ${mobile ? "20px" : "16px"};
				cursor: pointer;
				${focusRing}

				&:hover {
					background: ${theme.colors.backgrounds[1].background};
					color: ${ink};
				}
			}

			${Body} {
				${mobile
					? css`
							display: flex;
							flex-direction: column;
							gap: 18px;
						`
					: css`
							padding: 16px;
							max-height: 470px;
							overflow-y: auto;
						`}
			}

			${SectionLabelRow} {
				display: flex;
				align-items: baseline;
				justify-content: space-between;
				margin-bottom: 10px;
			}

			${SectionLabel} {
				font-size: ${mobile
					? theme.typography.fontSize.base
					: theme.typography.fontSize.small};
				font-weight: ${theme.typography.fontWeight.normal};
				text-transform: uppercase;
				letter-spacing: 0.1em;
				color: ${secondary};
			}

			${SelectedName} {
				font-size: ${theme.typography.fontSize.xLarge};
				color: ${ink};
			}

			${Caption} {
				display: flex;
				justify-content: space-between;
				margin-top: 10px;
			}

			${Divider} {
				height: 1px;
				margin: 20px 0 16px;
				border: 0;
				background: ${hairline};
			}

			${Swatches}, ${Tiles} {
				display: grid;
				margin: 0;
				padding: 0;
				list-style: none;
			}

			${Swatches} {
				grid-template-columns: repeat(${mobile ? 10 : 5}, 1fr);
				gap: ${mobile ? "6px" : "8px"};
			}

			${Swatch} {
				display: block;
				width: 100%;
				aspect-ratio: 1;
				padding: 0;
				border: 0;
				border-radius: 50%;
				box-shadow: ${unselectedRing};
				transition: box-shadow 120ms;
				cursor: pointer;
				${focusRing}

				&[aria-checked="true"] {
					box-shadow: ${selectedRing};
				}
			}

			${Tiles} {
				grid-template-columns: repeat(${mobile ? 4 : 3}, 1fr);
				gap: ${mobile ? "8px 6px" : theme.dimensions.margin.small};
			}

			${Tile} {
				display: flex;
				flex-direction: column;
				gap: 8px;
				width: 100%;
				padding: ${mobile ? 0 : "8px"};
				border: 0;
				border-radius: ${theme.dimensions.borderRadius};
				background: transparent;
				color: ${secondary};
				font-family: inherit;
				font-weight: ${theme.typography.fontWeight.light};
				text-align: ${mobile ? "center" : "left"};
				cursor: pointer;
				${focusRing}

				&:hover {
					background: ${mobile ? "transparent" : tileHoverFill};
				}

				&[aria-checked="true"] {
					color: ${ink};

					${Thumbnail} {
						box-shadow: ${selectedRing};
					}
				}
			}

			${Thumbnail} {
				display: block;
				width: 100%;
				aspect-ratio: ${mobile ? "1" : "4 / 3"};
				border-radius: ${theme.dimensions.borderRadius};
				background-size: 92px;
				background-position: center;
				box-shadow: ${unselectedRing};
				transition: box-shadow 120ms;
			}

			${TileLabel} {
				font-size: ${mobile
					? theme.typography.fontSize.base
					: theme.typography.fontSize.xLarge};
				line-height: ${mobile ? 1.3 : "20px"};
			}
		`;
	}}
`;
